import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, Select, MenuItem,
  FormControl, CircularProgress, Chip, Stack, Pagination,
  Checkbox, ListItemText, Divider, useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  Download as DownloadIcon,
  AccountBalanceWallet as TotalIcon,
  ReceiptLong as OrdersIcon,
  PhoneInTalk as PhoneIcon,
  Receipt as CheckIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
// Reuse the Revenue Report styling so the two reports look identical (per spec).
import styles from './RevenueReport.module.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtAmt = (n) => {
  const abs = Math.abs(Number(n) || 0);
  const str = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return Number(n) < 0 ? `($${str})` : `$${str}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const cleanNotes = (value) => {
  if (!value) return '';
  if (typeof value !== 'string') return String(value);
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      if (typeof parsed.notes === 'string' && parsed.notes.trim()) return parsed.notes.trim();
      if (Array.isArray(parsed.items) && parsed.items.length) {
        return parsed.items.map((i) => i?.description).filter(Boolean).join('; ');
      }
      return '';
    }
  } catch { /* not JSON — show as-is */ }
  return value;
};

const stripOrderPrefix = (v) => (typeof v === 'string' ? v.replace(/^ORD-/, '') : v);

const PAYMENT_METHOD_CHIP = {
  'Pay by Phone': { sx: { bgcolor: '#6a1b9a', color: '#fff', fontWeight: 600 } },
  'Pay by Check': { sx: { bgcolor: '#e65100', color: '#fff', fontWeight: 600 } },
};

const PAYMENT_STATUS_CHIP = {
  'Pending Phone Payment':    { sx: { bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 600, border: '1px solid #ce93d8' } },
  'Waiting on Check Payment': { sx: { bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, border: '1px solid #ffcc80' } },
  'Outstanding':              { sx: { bgcolor: '#eceff1', color: '#37474f', fontWeight: 600, border: '1px solid #b0bec5' } },
};

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, amount, count, amountColor = '#003063', iconBg = '#e8f0fe' }) => (
  <Paper elevation={0} className={styles.statCard}>
    <Box className={styles.statCardIcon} sx={{ bgcolor: iconBg }}>{icon}</Box>
    <Box>
      <Typography className={styles.statCardLabel}>{label}</Typography>
      <Typography className={styles.statCardAmount} sx={{ color: amountColor }}>
        {amount !== undefined ? fmtAmt(amount) : count}
      </Typography>
      {amount !== undefined && count !== undefined && (
        <Typography className={styles.statCardCount}>{count} Order{count !== 1 ? 's' : ''}</Typography>
      )}
    </Box>
  </Paper>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const OutstandingPaymentsReport = () => {
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');

  // Default to ALL outstanding (no date bound). Order Date is an optional filter.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [pmFilter, setPmFilter] = useState('all');   // all | phone | check
  const [psFilter, setPsFilter] = useState('all');   // all | pending-phone | waiting-check
  const [acctFilter, setAcctFilter] = useState([]);

  const [rows,    setRows]    = useState([]);
  const [summary, setSummary] = useState({ totalOutstanding: 0, outstandingOrders: 0, phoneAmount: 0, phoneCount: 0, checkAmount: 0, checkCount: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [hasRun,    setHasRun]    = useState(false);

  // Event multi-select (search box lives inside the dropdown)
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);
  const [eventSearch, setEventSearch] = useState('');
  const [debouncedEventSearch, setDebouncedEventSearch] = useState('');
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);

  const [accountCodes, setAccountCodes] = useState([]);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axiosPrivate.get('/api/events/projectgroups')
      .then(res => { if (!cancelled) setAccountCodes(res.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [axiosPrivate]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedEventSearch(eventSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [eventSearch]);

  useEffect(() => {
    if (!eventDropdownOpen) return;
    let cancelled = false;
    setIsLoadingEvents(true);
    axiosPrivate.get('/api/reports/events', { params: { search: debouncedEventSearch || undefined, limit: 50 } })
      .then(res => { if (!cancelled) setEventOptions(res.data || []); })
      .catch(() => { if (!cancelled) setEventOptions([]); })
      .finally(() => { if (!cancelled) setIsLoadingEvents(false); });
    return () => { cancelled = true; };
  }, [axiosPrivate, debouncedEventSearch, eventDropdownOpen]);

  const buildUrl = useCallback((fmt = 'json') => {
    const eventIds = selectedEvents.map((e) => e.EventsID).filter(Boolean);
    const p = new URLSearchParams({
      paymentMethod: pmFilter,
      paymentStatus: psFilter,
      accountCode: acctFilter.length ? acctFilter.join(',') : 'all',
      eventId:     eventIds.length   ? eventIds.join(',')   : 'all',
      format: fmt,
    });
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo)   p.set('dateTo', dateTo);
    return `/api/reports/outstanding?${p.toString()}`;
  }, [dateFrom, dateTo, pmFilter, psFilter, acctFilter, selectedEvents]);

  const runReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axiosPrivate.get(buildUrl('json'));
      setRows(res.data.rows || []);
      setSummary(res.data.summary || { totalOutstanding: 0, outstandingOrders: 0, phoneAmount: 0, phoneCount: 0, checkAmount: 0, checkCount: 0 });
      setHasRun(true);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      if ((res.data.rows || []).length === 0) {
        enqueueSnackbar('No outstanding orders found for these filters.', { variant: 'info' });
      }
    } catch {
      enqueueSnackbar('Failed to load outstanding payments report.', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, buildUrl, enqueueSnackbar]);

  useEffect(() => { runReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async (fmt) => {
    try {
      const res = await axiosPrivate.get(buildUrl(fmt), { responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Outstanding_Payments_Report.${fmt === 'csv' ? 'csv' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar('Export failed.', { variant: 'error' });
    }
  };

  // Pagination + sort over the full result set
  const totalCount = rows.length;
  const pageCount  = Math.ceil(totalCount / paginationModel.pageSize) || 1;
  const startIdx   = paginationModel.page * paginationModel.pageSize;
  const sortedRows = (() => {
    if (!sortModel.length) return rows;
    const { field, sort } = sortModel[0];
    if (!field || !sort) return rows;
    const dir = sort === 'desc' ? -1 : 1;
    const num = (v) => {
      const n = Number(String(v).replace(/[$,\s]/g, ''));
      return Number.isFinite(n) && String(v ?? '').trim() !== '' ? n : null;
    };
    return [...rows].sort((a, b) => {
      const an = num(a?.[field]), bn = num(b?.[field]);
      if (an !== null && bn !== null) return (an - bn) * dir;
      const as = String(a?.[field] ?? '').toLowerCase();
      const bs = String(b?.[field] ?? '').toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
  })();
  const pageRows = sortedRows.slice(startIdx, startIdx + paginationModel.pageSize);

  const columns = useMemo(() => [
    {
      field: 'orderId', headerName: 'Order ID', width: 108,
      renderCell: p => <Typography variant="body2" fontWeight={600}>{stripOrderPrefix(p.value)}</Typography>,
    },
    {
      field: 'orderCreatedDate', headerName: 'Order Created', width: 120,
      renderCell: p => <Typography variant="body2">{fmtDate(p.value)}</Typography>,
    },
    { field: 'customerName', headerName: 'Customer Name', flex: 1, minWidth: 150 },
    { field: 'eventName',    headerName: 'Event Name',    flex: 1.4, minWidth: 170 },
    {
      field: 'orderTotal', headerName: 'Order Total', width: 110,
      renderCell: p => <Typography variant="body2">{fmtAmt(p.value)}</Typography>,
    },
    {
      field: 'amountOutstanding', headerName: 'Amount Outstanding', width: 150,
      renderCell: p => (
        <Typography variant="body2" fontWeight={600} sx={{ color: '#c62828' }}>{fmtAmt(p.value)}</Typography>
      ),
    },
    {
      field: 'accountCode', headerName: 'Account Code', width: 130,
      renderCell: p => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{p.value || '—'}</Typography>
          {p.row.accountDescription && (
            <Typography variant="caption" color="text.secondary">{p.row.accountDescription}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'paymentMethod', headerName: 'Payment Method', width: 150,
      renderCell: p => {
        const cfg = PAYMENT_METHOD_CHIP[p.value];
        return cfg
          ? <Chip label={p.value} size="small" sx={{ ...cfg.sx, fontSize: 11 }} />
          : <Typography variant="body2">{p.value || '—'}</Typography>;
      },
    },
    {
      field: 'paymentStatus', headerName: 'Payment Status', width: 190,
      renderCell: p => {
        const cfg = PAYMENT_STATUS_CHIP[p.value];
        return p.value
          ? <Chip label={p.value} size="small" variant="outlined" sx={{ ...(cfg?.sx || {}), fontSize: 11 }} />
          : <Typography variant="body2">—</Typography>;
      },
    },
    {
      field: 'notes', headerName: 'Notes', flex: 1, minWidth: 140,
      renderCell: p => <Typography variant="body2">{cleanNotes(p.value) || '—'}</Typography>,
    },
  ], []);

  const headerBanner = (
    <div className="full-row light-blue">
      <div className="container-fluid">
        <div className="row text-white pt-3 pb-3">
          <div className="col-md-12">
            <h1 className="text-uppercase">Outstanding Payments Report</h1>
            <p>Event registrations and orders with an unpaid balance, including Pay by Check and Pay by Phone.</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <main>
      {headerBanner}

      <div className="full-row white">
        <Box className={styles.container}>

          {/* Back button */}
          <Box className={styles.backRow}>
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/admin/reporting')}>
              Back to Reports
            </Button>
          </Box>

          {/* Filter panel */}
          <Paper className={styles.filterPanel} elevation={0}>
            <Box className={styles.filtersGrid}>

              {/* Order Date range */}
              <Box className={styles.filterGroup}>
                <Typography className={styles.filterLabel}>Order Date</Typography>
                <Box className={styles.dateRange}>
                  <TextField type="date" size="small" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={styles.dateField} inputProps={{ max: dateTo || undefined }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>—</Typography>
                  <TextField type="date" size="small" value={dateTo} onChange={e => setDateTo(e.target.value)} className={styles.dateField} inputProps={{ min: dateFrom || undefined }} />
                </Box>
              </Box>

              {/* Payment Method */}
              <Box className={styles.filterGroup}>
                <Typography className={styles.filterLabel}>Payment Method</Typography>
                <FormControl size="small" className={styles.filterSelect}>
                  <Select value={pmFilter} onChange={e => setPmFilter(e.target.value)} displayEmpty>
                    <MenuItem value="all">All Payment Methods</MenuItem>
                    <MenuItem value="phone">Pay by Phone</MenuItem>
                    <MenuItem value="check">Pay by Check</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Payment Status */}
              <Box className={styles.filterGroup}>
                <Typography className={styles.filterLabel}>Payment Status</Typography>
                <FormControl size="small" className={styles.filterSelect}>
                  <Select value={psFilter} onChange={e => setPsFilter(e.target.value)} displayEmpty>
                    <MenuItem value="all">All Outstanding</MenuItem>
                    <MenuItem value="pending-phone">Pending Phone Payment</MenuItem>
                    <MenuItem value="waiting-check">Waiting on Check Payment</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Account Code (multi-select) */}
              <Box className={styles.filterGroup}>
                <Typography className={styles.filterLabel}>Account Code</Typography>
                <FormControl size="small" className={styles.filterSelect}>
                  <Select
                    multiple
                    value={acctFilter}
                    onChange={e => setAcctFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                    displayEmpty
                    renderValue={(selected) =>
                      !selected || selected.length === 0
                        ? 'All'
                        : selected.length === 1 ? selected[0] : `${selected.length} selected`
                    }
                  >
                    {(() => {
                      const withValue = accountCodes.map(a => ({
                        itemId:    a.ItemID,
                        codeValue: a.AccountingNumber || a.Name,
                        label:     a.AccountingNumber ? `${a.AccountingNumber} — ${a.Name}` : a.Name,
                      }));
                      const selected   = withValue.filter(a => acctFilter.indexOf(a.codeValue) > -1);
                      const unselected = withValue.filter(a => acctFilter.indexOf(a.codeValue) === -1);
                      const renderItem = (a, checked) => (
                        <MenuItem key={a.itemId} value={a.codeValue}>
                          <Checkbox checked={checked} />
                          <ListItemText primary={a.label} />
                        </MenuItem>
                      );
                      return [
                        selected.length > 0 && (
                          <Box key="acct-sel-hdr" sx={{ px: 2, py: 0.5, fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>
                            Selected ({selected.length})
                          </Box>
                        ),
                        ...selected.map(a => renderItem(a, true)),
                        selected.length > 0 && <Divider key="acct-sel-div" sx={{ my: 0.5 }} />,
                        ...unselected.map(a => renderItem(a, false)),
                      ];
                    })()}
                  </Select>
                </FormControl>
              </Box>

              {/* Event (multi-select; search inside dropdown) */}
              <Box className={`${styles.filterGroup} ${styles.eventFilterGroup}`}>
                <Typography className={styles.filterLabel}>Event</Typography>
                <FormControl size="small" className={styles.filterSelect}>
                  <Select
                    multiple
                    open={eventDropdownOpen}
                    onOpen={() => setEventDropdownOpen(true)}
                    onClose={() => { setEventDropdownOpen(false); setEventSearch(''); }}
                    displayEmpty
                    value={selectedEvents.map((e) => e.EventsID)}
                    onChange={() => {}}
                    renderValue={() =>
                      selectedEvents.length === 0
                        ? 'All'
                        : selectedEvents.length === 1 ? selectedEvents[0].BP_Title : `${selectedEvents.length} selected`
                    }
                    MenuProps={{ autoFocus: false, disableAutoFocusItem: true, PaperProps: { sx: { maxHeight: 420, minWidth: 480 } } }}
                  >
                    <Box
                      sx={{ p: 1, position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 2, borderBottom: '1px solid #eee' }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <TextField
                        autoFocus fullWidth size="small" placeholder="Search events…"
                        value={eventSearch}
                        onChange={(e) => setEventSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation(); }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        InputProps={{ endAdornment: isLoadingEvents ? <CircularProgress color="inherit" size={16} /> : null }}
                      />
                    </Box>

                    {selectedEvents.length > 0 && (
                      <Box sx={{ px: 2, pt: 1, pb: 0.5, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#666' }}>
                        Selected ({selectedEvents.length})
                      </Box>
                    )}
                    {selectedEvents.map((s) => (
                      <MenuItem
                        key={`sel-${s.EventsID}`}
                        value={s.EventsID}
                        onClick={() => setSelectedEvents((prev) => prev.filter((p) => Number(p.EventsID) !== Number(s.EventsID)))}
                        sx={{ whiteSpace: 'normal' }}
                      >
                        <Checkbox checked />
                        <ListItemText primary={s.BP_Title} />
                      </MenuItem>
                    ))}
                    {selectedEvents.length > 0 && <Divider sx={{ my: 0.5 }} />}

                    {!isLoadingEvents && eventOptions.length === 0 && (
                      <Box sx={{ p: 2, color: 'text.secondary', fontSize: 14 }}>
                        {debouncedEventSearch ? 'No matching events' : 'Type to search…'}
                      </Box>
                    )}

                    {eventOptions
                      .filter((ev) => !selectedEvents.some((s) => Number(s.EventsID) === Number(ev.EventsID)))
                      .map((ev) => (
                        <MenuItem
                          key={ev.EventsID}
                          value={ev.EventsID}
                          onClick={() => setSelectedEvents((prev) => [...prev, ev])}
                          sx={{ whiteSpace: 'normal' }}
                        >
                          <Checkbox checked={false} />
                          <ListItemText primary={ev.BP_Title} />
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>

            </Box>

            <Box className={styles.filterActions}>
              <Button
                variant="contained"
                onClick={runReport}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                className={styles.runBtn}
              >
                {isLoading ? 'Loading…' : 'Run Report'}
              </Button>
            </Box>
          </Paper>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#003063' }} />
            </Box>
          )}

          {!isLoading && hasRun && (
            <>
              {/* Stat cards */}
              <Box className={styles.statsRow}>
                <StatCard icon={<TotalIcon sx={{ color: '#c62828' }} />}  label="TOTAL OUTSTANDING"  amount={summary.totalOutstanding} amountColor="#c62828" iconBg="#ffebee" />
                <StatCard icon={<OrdersIcon sx={{ color: '#1565c0' }} />}  label="OUTSTANDING ORDERS" count={summary.outstandingOrders} amountColor="#1565c0" iconBg="#e8f0fe" />
                <StatCard icon={<PhoneIcon sx={{ color: '#6a1b9a' }} />}   label="PAY BY PHONE"       amount={summary.phoneAmount} count={summary.phoneCount} amountColor="#6a1b9a" iconBg="#f3e5f5" />
                <StatCard icon={<CheckIcon sx={{ color: '#e65100' }} />}   label="PAY BY CHECK"       amount={summary.checkAmount} count={summary.checkCount} amountColor="#e65100" iconBg="#fff3e0" />
              </Box>

              {/* Detail header + exports */}
              <Box className={styles.sectionHeader}>
                <Typography className={styles.sectionTitle}>DETAIL REPORT</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => handleDownload('csv')}   disabled={rows.length === 0} className={styles.exportBtn}>Export CSV</Button>
                  <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => handleDownload('excel')} disabled={rows.length === 0} className={styles.exportBtn}>Export Excel</Button>
                </Box>
              </Box>

              {rows.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <Typography color="text.secondary">No outstanding orders found for the selected filters.</Typography>
                </Paper>
              ) : (
                <>
                  <Paper className={styles.dataGridPaper} elevation={0}>
                    <DataGrid
                      rows={pageRows}
                      columns={columns}
                      getRowId={row => row.rowId}
                      autoHeight
                      getRowHeight={() => 'auto'}
                      hideFooterPagination
                      sortingMode="server"
                      sortModel={sortModel}
                      onSortModelChange={(m) => { setSortModel(m); setPaginationModel((prev) => ({ ...prev, page: 0 })); }}
                      disableRowSelectionOnClick
                      disableColumnFilter
                      disableDensitySelector
                      className="usersDataGrid"
                    />
                  </Paper>

                  <Paper className={styles.paginationPaper} elevation={0}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                      sx={{ width: '100%', flexWrap: 'wrap' }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Showing {totalCount > 0 ? startIdx + 1 : 0} to {Math.min(startIdx + paginationModel.pageSize, totalCount)} of {totalCount} rows
                      </Typography>

                      <Stack direction="row" alignItems="center" sx={{ gap: 2, order: { xs: -1, md: 0 }, '& .MuiPagination-ul': { flexWrap: 'wrap', justifyContent: 'center', rowGap: 1 } }}>
                        <Pagination
                          count={pageCount}
                          page={paginationModel.page + 1}
                          onChange={(_, p) => setPaginationModel(prev => ({ ...prev, page: p - 1 }))}
                          color="primary" variant="outlined" shape="rounded" showFirstButton showLastButton
                          siblingCount={isMobile ? 0 : 1}
                          boundaryCount={1}
                          className="usersPagination"
                        />
                      </Stack>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary">Show:</Typography>
                        <TextField
                          select size="small" value={paginationModel.pageSize}
                          onChange={e => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value) })}
                          SelectProps={{ native: true }}
                          sx={{ minWidth: 80 }}
                          className="pageSizeField"
                        >
                          {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                        </TextField>
                        <Typography variant="body2" color="text.secondary">per page</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </>
              )}
            </>
          )}

        </Box>
      </div>
    </main>
  );
};

export default OutstandingPaymentsReport;
